import {
    useEffect, useMemo, useState,
} from 'react';
import classNames from 'classnames';
import {
    DndContext, closestCenter, DragEndEvent, KeyboardSensor, Modifier, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { IProFormaUses, DevelopmentUnitOfMeasureTypes, IFormattedCashFlowPeriod } from 'views/ProFormaTable/types';
import { NSButton, NSCheckbox } from 'bricks';
import { numeralFormatterCurrency } from 'ns_libs/formatter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretRight, faPlus } from '@fortawesome/free-solid-svg-icons';
import RenderIf from 'components/RenderIf/RenderIf';
import { PRO_FORMA_UNIT_TO_FIELD_VALUES } from 'constants/unitTypes';
import { DEFAULT_SUBCATEGORY_NAME } from 'views/ProFormaTable/constants';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { useUsesTableContext } from '../context/UsesTableProvider';
import UsesTableSubcategory from './UsesTableSubcategory/UsesTableSubcategory';
import { useProFormaSocketContext } from '../../../socketContext/ProFormaSocketProvider';
import PeriodCellValue from '../PeriodCellValue/PeriodCellValue';

const restrictToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 });

export interface IUsesTableHighLevel {
    highLevel: IProFormaUses;
    unitType: DevelopmentUnitOfMeasureTypes;
    projectionPeriods: IFormattedCashFlowPeriod[];
    isPreview?: boolean;
}

const UsesTableHighLevel = ({
    highLevel, unitType, projectionPeriods, isPreview,
}: IUsesTableHighLevel) => {
    const {
        handleCreateBudgetClassification, handleUpdateBudgetClassifications, isBudgetClassificationLoading,
        showCents, periodCadence,
    } = useProFormaSocketContext();

    const {
        selectedCategories, expandedCategories, updateSelectedCategories,
        updateExpandedCategories, isProFormaReadOnly,
    } = useUsesTableContext();
    const [isHovered, setIsHovered] = useState(false);

    const subcategoriesSensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const canReorderSubcategories = !isPreview && !isProFormaReadOnly;

    const expandCategory = () => {
        if (!expandedCategories.highLevels.includes(highLevel.id)) {
            updateExpandedCategories(highLevel.id, null);
        }
    };

    const createSubcategory = () => {
        const postBody = {
            description: DEFAULT_SUBCATEGORY_NAME,
            parentId: highLevel.id,
        };
        handleCreateBudgetClassification(postBody, expandCategory);
    };

    const propSubcategoryIds = useMemo(
        () => (highLevel.children || []).map(subcategory => subcategory.id),
        [highLevel.children],
    );

    const [orderedSubcategoryIds, setOrderedSubcategoryIds] = useState<number[]>(propSubcategoryIds);

    // Adopt the server-driven order when it changes; same-order updates stay a no-op.
    useEffect(() => {
        setOrderedSubcategoryIds(prev => {
            if (
                prev.length === propSubcategoryIds.length
                && prev.every((id, index) => id === propSubcategoryIds[index])
            ) {
                return prev;
            }
            return propSubcategoryIds;
        });
    }, [propSubcategoryIds]);

    const orderedSubcategories = useMemo(() => {
        const byId = new Map((highLevel.children || []).map(subcategory => [subcategory.id, subcategory]));
        return orderedSubcategoryIds
            .map(id => byId.get(id))
            .filter((subcategory): subcategory is NonNullable<typeof subcategory> => Boolean(subcategory));
    }, [orderedSubcategoryIds, highLevel.children]);

    const budgetClassificationIds: number[] = [];
    orderedSubcategories.forEach(subcategory => {
        subcategory.children?.forEach(budgetClassification => {
            if (budgetClassification.isDeletable) {
                budgetClassificationIds.push(budgetClassification.id);
            }
        });
    });

    const subcategoryIds = orderedSubcategoryIds;

    const selectedUnitTotal = highLevel[PRO_FORMA_UNIT_TO_FIELD_VALUES[unitType]];
    let highLevelPeriods = highLevel.periods || [];
    if (periodCadence === Periods.ANNUALLY) highLevelPeriods = highLevel.yearlyPeriods || [];

    const handleSubcategoryDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        let computedNewIndex: number | null = null;
        setOrderedSubcategoryIds(prev => {
            const oldIndex = prev.indexOf(active.id as number);
            const newIndex = prev.indexOf(over.id as number);
            if (oldIndex === -1 || newIndex === -1) return prev;
            computedNewIndex = newIndex;
            return arrayMove(prev, oldIndex, newIndex);
        });
        if (computedNewIndex !== null) {
            handleUpdateBudgetClassifications([{ id: active.id as number, position: computedNewIndex }]);
        }
    };

    return (
        <>
            <tr
                className="NSTable__tbody__tr NSTable__tbody__tr--level-1"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <td
                    className="NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border text-dark"
                >
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex">
                            <RenderIf isTrue={!isProFormaReadOnly && !isPreview}>
                                <NSCheckbox
                                    id={`selectHighLevel-${highLevel.id}`}
                                    checked={selectedCategories.highLevels.includes(highLevel.id)}
                                    callback={() => updateSelectedCategories(highLevel.id, subcategoryIds, budgetClassificationIds)}
                                    containerClassName="custom-control-inline align-middle"
                                />
                            </RenderIf>
                            <div
                                role="presentation"
                                onClick={() => {
                                    updateExpandedCategories(highLevel.id, null);
                                }}
                            >
                                <RenderIf isTrue={Boolean(highLevel.children?.length)}>
                                    <FontAwesomeIcon
                                        className="icon mr-1 ReadOnlyWrapper--enable-pointer-events"
                                        icon={expandedCategories.highLevels.includes(highLevel.id) ? faCaretDown : faCaretRight}
                                        fixedWidth
                                    />
                                </RenderIf>
                                <span className={classNames({ 'ml-3': !highLevel.children?.length })}>{highLevel.description}</span>
                            </div>
                        </div>
                        <NSButton
                            icon={faPlus}
                            size="sm"
                            className={classNames('px-2 text-muted hover:text-dark bg-none hover:bg-light', { invisible: !isHovered })}
                            callback={createSubcategory}
                            isLoading={isBudgetClassificationLoading}
                        />
                    </div>
                </td>
                <td className="NSTable__tbody__tr__td" />
                <td className="NSTable__tbody__tr__td" />
                <td className="NSTable__tbody__tr__td" />
                <td className="NSTable__tbody__tr__td">
                    <div className="d-flex justify-content-end text-dark">
                        {selectedUnitTotal != null ? numeralFormatterCurrency(selectedUnitTotal) : '—'}
                    </div>
                </td>
                <td className="NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border pr-2">
                    <div className="d-flex justify-content-end text-dark">
                        {highLevel.amount != null ? numeralFormatterCurrency(highLevel.amount, showCents ? '$0,0.00' : '$0,0') : '—'}
                    </div>
                </td>
                {projectionPeriods?.map((period, index) => {
                    const targetPeriod = highLevelPeriods[index];

                    return (
                        <PeriodCellValue
                            key={`uses-empty-period-${period.value}`}
                            tooltipId={`uses-empty-period-${period.value}`}
                            period={period}
                            value={targetPeriod?.projected === null || highLevel.amount === null
                                ? '—'
                                : numeralFormatterCurrency(
                                    targetPeriod?.projected,
                                    showCents ? '$0,0.00' : '$0,0',
                                )}
                            cellClassName="NSTable__tbody__tr__td text-dark text-right"
                        />
                    );
                })}
            </tr>

            <RenderIf isTrue={expandedCategories.highLevels.includes(highLevel.id)}>
                <DndContext
                    sensors={subcategoriesSensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={handleSubcategoryDragEnd}
                >
                    <SortableContext items={subcategoryIds} strategy={verticalListSortingStrategy}>
                        {orderedSubcategories.map(subcategory => (
                            <UsesTableSubcategory
                                key={subcategory.id}
                                subcategory={subcategory}
                                unitType={unitType}
                                projectionPeriods={projectionPeriods}
                                isPreview={isPreview}
                                isSortable={canReorderSubcategories}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            </RenderIf>
        </>
    );
};

export default UsesTableHighLevel;
